import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { History, Plus } from 'lucide-react';
import { AdminButton, Badge, Card, DataTable, FilterBar, SectionHeader, Select, } from '../admin-ui';
import { usePricing, usePublishRate, useUsageMeta } from './ai-usage-api';
/**
 * Rate card.
 *
 * Rates are APPEND-ONLY: publishing a new value closes the previous row rather
 * than editing it. That is what keeps historical cost stable — every priced
 * usage record embeds the rate that produced it, so a change here can never
 * re-value spend that was already reported.
 *
 * This replaces the old "price book" authoring screen, which was built for the
 * earlier attempt's book-versioned model. Pricing is now model-centric: a rate
 * belongs to (provider, service, model, metric, dimension).
 */
const num = (v) => {
    const n = typeof v === 'string' ? Number(v) : v;
    return Number.isFinite(n) ? n : 0;
};
/** "$2.50 per 1M tokens" — the unit is what makes a bare rate meaningful. */
const formatRate = (r) => {
    var _a;
    const per = (_a = {
        1000000: '1M',
        10000: '10K',
        3600: 'hour',
        60: 'min',
        1000: '1K',
    }[num(r.unit_quantity)]) !== null && _a !== void 0 ? _a : num(r.unit_quantity).toLocaleString();
    const symbol = r.currency === 'INR' ? '₹' : '$';
    return `${symbol}${num(r.rate)} / ${per} ${r.billing_unit}`;
};
/** Canonical meter order — how an operator reads a token bill. */
const METRIC_ORDER = [
    'input_tokens', 'cached_input_tokens', 'output_tokens',
    'input_text_tokens', 'cached_input_text_tokens', 'output_text_tokens',
    'input_audio_tokens', 'cached_input_audio_tokens', 'output_audio_tokens',
];
const groupRates = (rows, includeVersion) => {
    const byKey = new Map();
    rows.forEach((r) => {
        var _a, _b, _c, _d;
        // With history shown, a superseded version must not merge into the current
        // one — they are different prices for the same meter.
        const key = [
            r.provider, r.service_type,
            (_a = r.model) !== null && _a !== void 0 ? _a : '',
            (_b = r.tier) !== null && _b !== void 0 ? _b : '',
            (_c = r.region) !== null && _c !== void 0 ? _c : '',
            (_d = r.sku) !== null && _d !== void 0 ? _d : '',
            includeVersion ? r.pricing_version : '',
            includeVersion ? String(r.active) : '',
        ].join('|');
        let group = byKey.get(key);
        if (!group) {
            group = {
                key,
                provider: r.provider,
                service_type: r.service_type,
                model: r.model,
                tier: r.tier,
                pricing_version: r.pricing_version,
                active: r.active,
                rates: [],
            };
            byKey.set(key, group);
        }
        group.rates.push(r);
    });
    byKey.forEach((g) => {
        g.rates.sort((a, b) => {
            const ia = METRIC_ORDER.indexOf(a.metric);
            const ib = METRIC_ORDER.indexOf(b.metric);
            if (ia !== -1 || ib !== -1)
                return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
            return a.metric.localeCompare(b.metric);
        });
    });
    return Array.from(byKey.values());
};
export default function PricingPanel() {
    var _a, _b, _c;
    const [provider, setProvider] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [adding, setAdding] = useState(false);
    const { data: meta } = useUsageMeta();
    const params = {};
    if (provider)
        params.provider = provider;
    if (showHistory)
        params.history = 'true';
    const rates = usePricing(params);
    /**
     * One row per MODEL, not per rate row.
     *
     * A rate belongs to (provider, service, model, metric), so a text model needs
     * three rows — input, cached input, output — and a realtime model seven. The
     * table repeated the model name once per metric, which read as duplicate
     * models. The rates themselves stay separate rows in the database; only the
     * presentation folds, because each metric genuinely has its own price.
     *
     * Metrics are NOT columns: which meters exist depends on the service (tokens
     * for an LLM, characters for TTS, seconds for STT), so a fixed column set
     * would be mostly empty. They are listed inside the row instead.
     */
    const grouped = groupRates((_b = (_a = rates.data) === null || _a === void 0 ? void 0 : _a.rates) !== null && _b !== void 0 ? _b : [], showHistory);
    const columns = [
        {
            key: 'model',
            header: 'Model / service',
            render: (g) => {
                var _a;
                return (_jsxs("span", { className: 'flex min-w-0 flex-col', children: [_jsx("span", { className: 'truncate font-medium text-dash-text', children: (_a = g.model) !== null && _a !== void 0 ? _a : g.service_type }), _jsxs("span", { className: 'truncate text-[11px] text-dash-muted', children: [g.provider, " \u00B7 ", g.service_type, g.tier ? ` · ${g.tier}` : ''] })] }));
            },
        },
        {
            key: 'meters',
            header: 'Meters',
            render: (g) => (_jsx("span", { className: 'flex flex-wrap gap-x-4 gap-y-1', children: g.rates.map((r) => (_jsxs("span", { className: 'inline-flex min-w-0 flex-col leading-tight', children: [_jsx("span", { className: 'text-[11px] text-dash-muted', children: (r.dimension || r.metric).replace(/_/g, ' ') }), _jsx("span", { className: 'whitespace-nowrap font-medium tabular-nums text-dash-text', children: formatRate(r) })] }, r.rate_id))) })),
        },
        {
            key: 'version',
            header: 'Version',
            align: 'right',
            hideOnMobile: true,
            render: (g) => (_jsxs("span", { className: 'flex flex-col items-end gap-0.5', children: [_jsx("span", { className: 'text-[11.5px] text-dash-muted', children: g.pricing_version }), !g.active && _jsx(Badge, { tone: 'neutral', children: "superseded" })] })),
        },
    ];
    return (_jsxs("div", { className: 'space-y-4', children: [_jsxs(Card, { children: [_jsx(SectionHeader, { title: 'Rate card', subtitle: 'Publishing a rate closes the previous one. Historical cost never moves \u2014 each usage record keeps the rate that priced it.', action: _jsx(AdminButton, { tone: 'primary', icon: _jsx(Plus, { className: 'h-4 w-4' }), onClick: () => setAdding(true), children: "Publish rate" }) }), _jsxs(FilterBar, { children: [_jsx(Select, { label: 'Provider', value: provider, onChange: setProvider, options: [
                                    { value: '', label: 'All providers' },
                                    ...((_c = meta === null || meta === void 0 ? void 0 : meta.providers) !== null && _c !== void 0 ? _c : []).map((p) => ({ value: p, label: p })),
                                ] }), _jsx(AdminButton, { icon: _jsx(History, { className: 'h-4 w-4' }), onClick: () => setShowHistory((v) => !v), children: showHistory ? 'Active only' : 'Show history' })] }), _jsx(DataTable, { columns: columns, rows: grouped, rowKey: (g) => g.key, loading: rates.isLoading, empty: 'No rates configured. Usage will record correctly but stay unpriced.' })] }), adding && _jsx(PublishRateDialog, { onClose: () => setAdding(false) })] }));
}
function PublishRateDialog({ onClose }) {
    var _a;
    const publish = usePublishRate();
    const { data: meta } = useUsageMeta();
    const [error, setError] = useState(null);
    const [form, setForm] = useState({
        provider: 'openai',
        service_type: 'llm',
        model: '',
        metric: 'input_tokens',
        dimension: 'input',
        billing_unit: 'tokens',
        unit_quantity: '1000000',
        rate: '',
        currency: 'USD',
    });
    const set = (k, v) => setForm((f) => (Object.assign(Object.assign({}, f), { [k]: v })));
    const field = (label, key, placeholder) => (_jsxs("label", { className: 'flex min-w-0 flex-col gap-1', children: [_jsx("span", { className: 'text-[11px] font-medium uppercase tracking-wide text-dash-muted', children: label }), _jsx("input", { value: form[key], placeholder: placeholder, onChange: (e) => set(key, e.target.value), className: 'h-9 max-aiMd:h-11 rounded-lg border border-dash-border bg-dash-surface px-2.5 text-[12.5px] text-dash-text outline-none transition-colors focus:border-dash-accent' })] }));
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: 'fixed inset-0 z-[80] bg-black/50', onClick: onClose, "aria-hidden": 'true' }), _jsxs("div", { role: 'dialog', "aria-label": 'Publish a rate', className: 'fixed left-1/2 top-1/2 z-[81] max-h-[90vh] w-[calc(100vw-24px)] max-w-[520px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-dash-border bg-dash-bg2 p-4', children: [_jsx("h3", { className: 'text-[14px] font-semibold text-dash-text', children: "Publish a rate" }), _jsx("p", { className: 'mt-0.5 text-[12px] text-dash-muted', children: "A new version is created and the current rate for this meter is closed." }), error && (_jsx("div", { className: 'mt-3 rounded-lg bg-dash-dangerSoft px-3 py-2 text-[12px] text-dash-dangerInk', children: error })), _jsxs("div", { className: 'mt-3 grid grid-cols-1 gap-2.5 aiSm:grid-cols-2', children: [_jsx(Select, { label: 'Provider', value: form.provider, onChange: (v) => set('provider', v), options: ((_a = meta === null || meta === void 0 ? void 0 : meta.providers) !== null && _a !== void 0 ? _a : ['openai']).map((p) => ({ value: p, label: p })) }), field('Service type', 'service_type', 'llm / realtime / stt / tts'), field('Model', 'model', 'gpt-4o'), field('Metric', 'metric', 'input_tokens'), field('Dimension', 'dimension', 'input / output / cached_input'), field('Billing unit', 'billing_unit', 'tokens / characters / seconds'), field('Per (unit quantity)', 'unit_quantity', '1000000'), field('Rate', 'rate', '2.50'), _jsx(Select, { label: 'Currency', value: form.currency, onChange: (v) => set('currency', v), options: [
                                    { value: 'USD', label: 'USD' },
                                    { value: 'INR', label: 'INR' },
                                ] })] }), _jsx("p", { className: 'mt-2 text-[11px] text-dash-muted', children: "Provider currency is stored as billed; a USD figure is derived for comparison." }), _jsxs("div", { className: 'mt-4 flex justify-end gap-2', children: [_jsx(AdminButton, { onClick: onClose, children: "Cancel" }), _jsx(AdminButton, { tone: 'primary', disabled: !form.rate || !form.metric, onClick: () => publish.mutate(form, {
                                    onSuccess: onClose,
                                    onError: (e) => setError((e === null || e === void 0 ? void 0 : e.detail) || 'Could not publish this rate'),
                                }), children: "Publish" })] })] })] }));
}
